import { useState } from "react";
import type { FormEvent } from "react";
import type { CommentItem } from "@/types/content";
import { topicComments, defaultComments } from "@/mocks/comments";

type CommentSectionProps = {
  topicId: string;
  initialCount: number;
};

export default function CommentSection({ topicId, initialCount }: CommentSectionProps) {
  const seed = (topicComments as Record<string, CommentItem[]>)[topicId] ?? defaultComments;
  const [comments, setComments] = useState<CommentItem[]>(seed);
  const [draft, setDraft] = useState("");
  const [liked, setLiked] = useState<string[]>([]);
  const [notice, setNotice] = useState("");

  const totalCount = initialCount + (comments.length - seed.length);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = draft.trim();
    if (value.length < 2) {
      setNotice("再多写几个字吧，至少 2 个字。");
      return;
    }
    if (value.length > 300) {
      setNotice("评论请控制在 300 字以内。");
      return;
    }
    const newComment: CommentItem = {
      id: `local-${Date.now()}`,
      user: "我",
      time: "刚刚",
      content: value,
      likes: 0,
    };
    setComments([newComment, ...comments]);
    setDraft("");
    setNotice("已发布，当前浏览器可见。");
  };

  const toggleLike = (id: string) => {
    setLiked((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  };

  return (
    <section id="comments" className="rounded-2xl bg-background-100 border border-background-200 p-5 md:p-7">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-lg md:text-xl font-black text-foreground-950 font-heading">
          <span className="w-7 h-7 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center">
            <i className="ri-chat-3-line"></i>
          </span>
          评论区热议
        </h2>
        <span className="text-sm text-foreground-500">
          共 <strong className="font-bold text-foreground-900">{totalCount}</strong> 条讨论
        </span>
      </div>

      <form onSubmit={handleSubmit} className="mt-5 rounded-2xl bg-background-50 border border-background-200 p-4">
        <div className="flex gap-3">
          <span className="w-9 h-9 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center shrink-0 font-bold">
            我
          </span>
          <textarea
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            rows={3}
            maxLength={300}
            placeholder="说说你的看法，理性表达更容易被接茬…"
            className="flex-1 resize-none bg-transparent text-sm leading-relaxed text-foreground-900 placeholder:text-foreground-400 outline-none"
          />
        </div>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <span className="text-xs text-foreground-400">{draft.length}/300</span>
          <div className="flex items-center gap-3">
            {notice && <span className="text-xs text-primary-600">{notice}</span>}
            <button
              type="submit"
              className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-bold whitespace-nowrap hover:bg-primary-600 transition-colors"
            >
              <i className="ri-send-plane-fill"></i>
              发表观点
            </button>
          </div>
        </div>
      </form>

      <ul className="mt-6 flex flex-col gap-5">
        {comments.map((comment) => {
          const isLiked = liked.includes(comment.id);
          return (
            <li key={comment.id} className="flex gap-3">
              <span className="w-9 h-9 rounded-full bg-secondary-100 text-secondary-800 flex items-center justify-center shrink-0 font-bold">
                {comment.user.slice(0, 1)}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="text-sm font-bold text-foreground-950">{comment.user}</span>
                  <span className="text-xs text-foreground-400">{comment.time}</span>
                </div>
                <p className="mt-1.5 text-sm leading-relaxed text-foreground-700">{comment.content}</p>
                <div className="mt-2 flex items-center gap-4 text-xs text-foreground-500">
                  <button
                    type="button"
                    onClick={() => toggleLike(comment.id)}
                    aria-pressed={isLiked}
                    className={`inline-flex items-center gap-1 transition-colors ${
                      isLiked ? "text-primary-600 font-bold" : "hover:text-primary-600"
                    }`}
                  >
                    <i className={isLiked ? "ri-thumb-up-fill" : "ri-thumb-up-line"}></i>
                    {comment.likes + (isLiked ? 1 : 0)}
                  </button>
                  <button
                    type="button"
                    onClick={() => setNotice("回复功能已记录你的兴趣，后续话题会优先整理相关讨论。")}
                    className="inline-flex items-center gap-1 hover:text-primary-600 transition-colors"
                  >
                    <i className="ri-chat-1-line"></i>
                    回复
                  </button>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}